/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress } from "@mui/material";
import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import UserContext from "../../../context/User";
import { useRestaurant } from "../../../hooks";
import CartItem from "./CartItem";
import PricingView from "./PricingView";
import { useTranslation } from "react-i18next";
import { VariationModal } from "../../Modals";
import { pushNestedSelectionLines } from "../../../utils/nestedCartLines";
import { modifierOptionId } from "../../../utils/modifierOptionId";
import { cateringItemIds, cateringNotice, cateringSettingsOf, isCateringCart } from "../../../utils/catering";
import { useLocationContext } from "../../../context/Location";
import { useTimeSelect } from "../../../context/Time";
import { useOrderMode, ORDER_MODES } from "../../../context/OrderMode";
import { shortAddress } from "../../../utils/shortAddress";
import { dealKind, dealReward, dealsForCart, dealThreshold, dealNeedsCode } from "../../../utils/dealText";
import { CartPromoCode, CartUpsell, DealProgress } from "./CartExtras";
import { C, RADIUS, SHADOW, TYPE } from "../../../utils/designTokens";

/** The white cart card, shared by the full cart and the empty one. */
export const cartPanelSx = (flat) => ({
  bgcolor: C.bg,
  borderRadius: flat ? 0 : RADIUS.card,
  border: flat ? 0 : `1px solid ${C.border}`,
  boxShadow: flat ? "none" : SHADOW.float,
  p: { xs: 2, sm: 2.5 },
});

const PAD = { xs: 2, sm: 2.5 };

/** "Your order" + an item-count pill. */
export function CartHeading({ count, title = "Your order", id }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box component="h2" id={id} sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>
        {title}
      </Box>
      {count > 0 && (
        <Box component="span" sx={{ ...TYPE.label, px: 1.25, py: 0.5, borderRadius: RADIUS.pill, bgcolor: C.surface2, color: C.ink }}>
          {`${count} ${count === 1 ? "item" : "items"}`}
        </Box>
      )}
    </Box>
  );
}

// Price a modifier option for a given quantity, honoring the group's duplicate
// pricing mode. Mirrors getDuplicateTotal in VariationModal so the cart total
// matches the customize modal.
//  - FIRST_FREE_THEN_CHARGED: 1st unit free, rest at basePrice
//  - INCREMENTAL / CUSTOM: per-unit prices from duplicatePrices { "1": p1, ... }
//  - SAME / default: basePrice * qty
function computeOptionPrice(basePrice, qty, addon) {
  if (qty <= 0) return 0;
  const mode = addon?.allowDuplicates ? addon?.duplicatePricingMode : null;

  if (mode === "FIRST_FREE_THEN_CHARGED") {
    return basePrice * Math.max(0, qty - 1);
  }

  if ((mode === "INCREMENTAL" || mode === "CUSTOM") && addon?.duplicatePrices) {
    const tiers = typeof addon.duplicatePrices === "string"
      ? JSON.parse(addon.duplicatePrices) : addon.duplicatePrices;
    let total = 0;
    for (let i = 1; i <= qty; i++) {
      total += resolveTierPrice(tiers[String(i)], basePrice);
    }
    return total;
  }

  return basePrice * qty;
}

// Resolve a single Nth unit's price under an INCREMENTAL tier rule (relative
// to base): { mode: SAME|FREE|PLUS, extra }. Legacy bare numbers = absolute.
function resolveTierPrice(tier, basePrice) {
  if (tier === undefined || tier === null) return basePrice;
  if (typeof tier === "number") return tier;
  if (typeof tier === "string") {
    const n = parseFloat(tier);
    return Number.isNaN(n) ? basePrice : n;
  }
  const m = tier.mode || "SAME";
  if (m === "FREE") return 0;
  if (m === "PLUS") return basePrice + (parseFloat(tier.extra) || 0);
  if (m === "MINUS") return Math.max(0, basePrice - (parseFloat(tier.extra) || 0));
  return basePrice;
}

function CartView(props) {
  const { t } = useTranslation();
  const [loadingData, setLoadingData] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAddonData, setEditAddonData] = useState(null);
  // promos: the restaurant's deals (progress bar + checkout note); upsell +
  // onAddItem: the "Add something small" pills; flat: drawn inside the phone
  // sheet, so no card of its own.
  // maxHeight: the panel's ceiling; past it the middle scrolls and the
  // summary + checkout button stay pinned at the bottom.
  const { promos = [], upsell = [], onAddItem, flat = false, maxHeight } = props;
  const { location } = useLocationContext();
  const { mode: timeMode, scheduledFor } = useTimeSelect();
  const { orderMode } = useOrderMode() || {};
  const isPickup = orderMode === ORDER_MODES.PICKUP;
  const {
    clearCart,
    restaurant: cartRestaurant,
    cart,
    cartCount,
    addQuantity,
    removeQuantity,
    updateCart,
    deleteItem,
    getCartMeta,
  } = useContext(UserContext);
  // A food-truck stop's cart is priced from that stop's menu.
  const cartStopId = (cartRestaurant && getCartMeta()?.stopId) || null;
  const { data, loading: restaurantLoading } = useRestaurant(cartRestaurant, undefined, cartStopId);
  const restaurantData = data?.restaurant ?? null;
  // A catering cart (backend migration 131) is ordered ahead: the header says
  // so and shows the notice, not today's prep time.
  const cateringIds = useMemo(() => cateringItemIds(restaurantData), [restaurantData]);
  const cateringCart = isCateringCart(cart, cateringIds);

  // foods/addons/options built during didFocus — store for edit use
  const [menuData, setMenuData] = useState({ foods: [], addons: [], options: [] });
  const pendingDeleteKeyRef = useRef(null);

  const toggleEditModal = useCallback(() => {
    setEditModalOpen((prev) => !prev);
  }, []);

  // Build a signature of the cart's *content* (not just length) so an
  // edit-in-place that keeps the count the same still re-derives titles and
  // prices. We exclude derived fields (price, foodTitle, …) so updateCart's
  // own writes don't trigger an infinite loop.
  const cartSignature = (cart || [])
    .map((c) => `${c.key}|${c._id}|${c.variation?._id}|${(c.addons || [])
      .map((a) => `${a._id}:${(a.options || []).map((o) => `${o._id}x${o._qty || 1}`).join(',')}`)
      .join(';')}|${c.specialInstructions || ''}|${c.quantity}`)
    .join('||');

  useEffect(() => {
    if (restaurantLoading) return;
    if (!restaurantData) { setLoadingData(false); return; }
    didFocus();
  }, [restaurantData, cartSignature, restaurantLoading]);

  const didFocus = async () => {
    // Check if using advanced menus
    const useAdvancedMenus = restaurantData?.advancedMenus?.some(
      (m) => m.isActive && m.items && m.items.length > 0
    );

    let foods = [];
    let addons = [];
    let options = [];

    if (useAdvancedMenus) {
      // Transform advanced menu data (same as MRestaurantCart)
      const addonsSet = new Set();
      const optionsSet = new Set();

      restaurantData.advancedMenus.forEach((menu) => {
        if (!menu.isActive) return;
        // Per-menu markup + menu-scoped id prefix — MUST mirror RestaurantDetail's
        // transform so the menu-scoped ids stored on the cart line match what we
        // rebuild here, and each menu carries its own marked-up component prices.
        const menuPct = parseFloat(menu.markupPercent || 0);
        const mk = (id) => `m${menu._id}_${id}`;
        // Apply same filtering as RestaurantDetail
        const activeItems = (menu.items || []).filter(
          (i) => i.isActive && i.isAvailable !== false && !i.is86ed
        );
        activeItems.forEach((item) => {
          // Build variations
          const variations = [];
          if (item.pricingMode === "VARIANTS" && item.variantPrices) {
            const entries = typeof item.variantPrices === "string"
              ? Object.entries(JSON.parse(item.variantPrices))
              : Object.entries(item.variantPrices);
            entries.forEach(([key, value]) => {
              variations.push({
                _id: `${item._id}_${key}`,
                title: key,
                price: parseFloat(value),
              });
            });
          } else {
            variations.push({
              _id: `${item._id}_default`,
              title: "Regular",
              price: parseFloat(item.basePrice || 0),
            });
          }

          // IDs of the addon/option groups this specific item is linked to, so
          // editing the item shows only ITS groups (not every group in the menu).
          const itemAddonGroupIds = [
            ...(item.modifierGroups || []).map((mg) => mk(mg._id.toString())),
            ...(item.optionGroups || []).map((og) => mk(`og_${og._id}`)),
          ];

          foods.push({
            _id: item._id.toString(),
            menuId: menu._id.toString(),
            markupPercent: menuPct,
            title: item.name,
            // Translated names travel with the line so the cart reads the same
            // as the menu the customer ordered from.
            altName1: item.alternateName1 || null,
            altName2: item.alternateName2 || null,
            variations,
            addonGroupIds: itemAddonGroupIds,
          });

          // Collect modifier groups
          item.modifierGroups?.forEach((mg) => {
            const mgId = mk(mg._id.toString());
            const mgOptionIds = [];

            mg.modifiers?.forEach((gm) => {
              if (gm.isHidden) return;
              const mod = gm.modifier;
              if (!mod || mod.libraryStatus?.is86ed) return;
              const modId = modifierOptionId(mk, gm, mod._id, false);
              mgOptionIds.push(modId);
              if (!optionsSet.has(modId)) {
                optionsSet.add(modId);
                let price = parseFloat(gm.fixedPrice || 0);
                let variantPricesMap = null;

                if (gm.priceOverrideMode === "VARIANT_BASED" && gm.variantPrices) {
                  variantPricesMap = typeof gm.variantPrices === "string"
                    ? JSON.parse(gm.variantPrices)
                    : gm.variantPrices;
                  const vals = Object.values(variantPricesMap).map((v) => parseFloat(v));
                  if (vals.length > 0) price = Math.min(...vals);
                } else if (gm.priceOverrideMode === "FREE") {
                  price = 0;
                }

                options.push({
                  _id: modId,
                  title: mod.name,
                  price,
                  variantPrices: variantPricesMap,
                });
              }
            });

            if (!addonsSet.has(mgId)) {
              addonsSet.add(mgId);
              addons.push({
                _id: mgId,
                title: mg.name,
                quantityMinimum: mg.isRequired ? (mg.minSelections || 1) : 0,
                quantityMaximum: mg.maxSelections || mgOptionIds.length,
                allowDuplicates: mg.allowDuplicates || false,
                maxDuplicates: mg.maxDuplicates || null,
                duplicatePricingMode: mg.duplicatePricingMode || null,
                duplicatePrices: (mg.duplicatePricingMode === 'INCREMENTAL' || mg.duplicatePricingMode === 'CUSTOM') && mg.duplicatePrices
                  ? (typeof mg.duplicatePrices === 'string' ? JSON.parse(mg.duplicatePrices) : mg.duplicatePrices)
                  : null,
                options: mgOptionIds,
                // Flat group cost, charged once when >=1 option in this group is picked.
                additionalCost: parseFloat(mg.additionalCost || 0),
              });
            }
          });

          // Collect option groups
          item.optionGroups?.forEach((og) => {
            const ogId = mk(`og_${og._id}`);
            const ogOptionIds = [];

            og.choices?.forEach((choice, idx) => {
              const choiceId = mk(`og_${og._id}_choice_${idx}`);
              ogOptionIds.push(choiceId);
              if (!optionsSet.has(choiceId)) {
                optionsSet.add(choiceId);
                options.push({
                  _id: choiceId,
                  title: choice,
                  price: 0,
                });
              }
            });

            if (!addonsSet.has(ogId)) {
              addonsSet.add(ogId);
              addons.push({
                _id: ogId,
                title: og.name,
                quantityMinimum: og.isRequired ? 1 : 0,
                quantityMaximum: og.selectionType === "MULTI" ? (og.choices?.length || 99) : 1,
                options: ogOptionIds, // IDs of options belonging to this group
              });
            }
          });
        });
      });

      // Merge with legacy if exists
      if (restaurantData.addons) {
        addons = [...restaurantData.addons, ...addons];
      }
      if (restaurantData.options) {
        options = [...restaurantData.options, ...options];
      }
    } else {
      // Legacy menu system
      foods = restaurantData.categories.map((c) => c.foods.flat()).flat();
      addons = restaurantData.addons || [];
      options = restaurantData.options || [];
    }

    setMenuData({ foods, addons, options });

    try {
      if (cart && cartCount) {
        const transformCart = cart.map((cartItem) => {
          // The same item id exists once per menu (each carrying that menu's
          // marked-up price). Match the menu the line was ordered from FIRST —
          // matching by _id alone returns the first menu's copy (e.g. the 0%
          // Breakfast price) and the line shows the wrong, un-marked-up amount.
          // Fall back to _id-only for legacy lines that have no menuId.
          const foodItem =
            foods.find((food) => food._id === cartItem._id && String(food.menuId) === String(cartItem.menuId))
            || foods.find((food) => food._id === cartItem._id);
          if (!foodItem) return null;
          const variationItem = foodItem.variations.find(
            (variation) => variation._id === cartItem.variation._id
          );
          if (!variationItem) return null;
          const foodItemTitle = `${foodItem.title}${
            variationItem.title ? `(${variationItem.title})` : ""
          }`;
          // Every price component (variation + add-ons) already arrives marked up
          // AND rounded per the menu's rule from the backend, so the cart line is
          // simply their sum — no further rounding.
          // Coerce to number; defensive against missing/null prices on legacy
          // or advanced-menu variations so totals never render as NaN.
          let foodItemPrice = parseFloat(variationItem.price) || 0;
          let optionTitles = [];
          let optionsWithPrices = []; // NEW: Store options with their prices
          if (cartItem.addons) {
            cartItem.addons.forEach((addon) => {
              const cartAddon = addons.find((add) => add._id === addon._id);
              if (!cartAddon) return null;
              const groupFee = parseFloat(cartAddon.additionalCost) || 0;
              if (groupFee > 0) foodItemPrice += groupFee;
              // Group header (name + flat fee) — mirrors the order summary layout.
              if ((addon.options || []).length > 0 || groupFee > 0) {
                optionTitles.push(cartAddon.title || 'Group');
                optionsWithPrices.push({ title: cartAddon.title || 'Group', price: groupFee > 0 ? groupFee.toFixed(2) : '', level: 0, header: true });
              }
              addon.options.forEach((option) => {
                // Item-as-modifier: price from the saved option (carries its own
                // price + nested selections); it isn't in the flattened `options`.
                if (option.isItemMember) {
                  const itemPrice = parseFloat(option.price) || 0;
                  foodItemPrice += itemPrice;
                  optionTitles.push(option.title);
                  optionsWithPrices.push({ title: `+ ${option.title}`, price: itemPrice > 0 ? itemPrice.toFixed(2) : '', level: 1 });
                  foodItemPrice += parseFloat(option.nested?.subtotal) || 0;
                  pushNestedSelectionLines(option.nested?.childSelections, optionTitles, optionsWithPrices);
                  return;
                }
                const cartOption = options.find(
                  (opt) => opt._id === option._id
                );
                if (!cartOption) return null;

                // Get base price for this option (check for variant-specific pricing).
                // Every parseFloat is guarded so a missing/null variantPrices entry
                // can't poison foodItemPrice and show up as $NaN in the cart.
                let basePrice = parseFloat(cartOption.price) || 0;
                if (cartOption.variantPrices && variationItem.title) {
                  const vp = cartOption.variantPrices;
                  if (vp[variationItem.title] !== undefined && vp[variationItem.title] !== null) {
                    const candidate = parseFloat(vp[variationItem.title]);
                    if (!Number.isNaN(candidate)) basePrice = candidate;
                  } else {
                    const key = Object.keys(vp).find(
                      (k) => k.toLowerCase() === variationItem.title.toLowerCase()
                    );
                    if (key && vp[key] !== null) {
                      const candidate = parseFloat(vp[key]);
                      if (!Number.isNaN(candidate)) basePrice = candidate;
                    }
                  }
                }

                // Handle duplicate-quantity options, honoring the group's
                // pricing mode (e.g. FIRST_FREE_THEN_CHARGED) so the cart total
                // matches what the customize modal showed.
                const qty = parseInt(option._qty, 10) || 1;
                let optionPrice = computeOptionPrice(basePrice, qty, cartAddon);
                if (!Number.isFinite(optionPrice)) optionPrice = 0;

                foodItemPrice += optionPrice;

                const optionDisplayTitle = qty > 1 ? `${cartOption.title} (x${qty})` : cartOption.title;
                optionTitles.push(optionDisplayTitle);
                optionsWithPrices.push({ title: `+ ${optionDisplayTitle}`, price: optionPrice > 0 ? optionPrice.toFixed(2) : '', level: 1 });
                // A plain modifier can carry follow-up groups of its own.
                if (option.nested?.childSelections?.length) {
                  // Charged per unit of this option, as the server does.
                  foodItemPrice += (parseFloat(option.nested.subtotal) || 0) * qty;
                  pushNestedSelectionLines(option.nested.childSelections, optionTitles, optionsWithPrices, 2, qty);
                }
              });
            });
          }
          // Components are already rounded per-component (backend) — the line is
          // their sum; the bare variation is shown as-is.
          const roundedLine = Number.isFinite(foodItemPrice) ? foodItemPrice : 0;
          return {
            ...cartItem,
            title: foodItemTitle,
            foodTitle: foodItem.title,
            foodAltName1: foodItem.altName1 || null,
            foodAltName2: foodItem.altName2 || null,
            variationTitle: variationItem.title,
            variationPrice: (parseFloat(variationItem.price) || 0).toFixed(2),
            optionTitles,
            optionsWithPrices, // NEW: Options with individual prices
            price: roundedLine.toFixed(2),
          };
        });
        const updatedItems = transformCart.filter((item) => item);
        // Only touch the cart when the underlying cart is actually empty.
        // If the customer has items but the transform briefly couldn't
        // resolve any (stale menu data right after an edit, mismatched IDs,
        // etc.) we must NOT call clearCart() OR updateCart([]) — either one
        // wipes the cart and the customer loses their item.
        if (updatedItems.length === 0) {
          if (!cart || cart.length === 0) {
            await clearCart();
          }
          // else: keep the existing cart, skip overwriting with [].
        } else {
          await updateCart(updatedItems);
        }
        setLoadingData((prev) => {
          if (prev) return false;
          else return prev;
        });
        if (transformCart.length !== updatedItems.length) {
          props.showMessage({
            type: t("warning"),
            message: t("warningText"),
          });
        }
      }
    } catch (e) {
      props.showMessage({
        type: "error",
        message: e.message,
      });
    } finally {
      setLoadingData((prev) => {
        if (prev) return false;
        else return prev;
      });
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ ...cartPanelSx(flat), display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
        <CircularProgress size={28} sx={{ color: C.red }} aria-label="Loading your order" />
      </Box>
    );
  }

  const prepTime = restaurantData?.preparationTime ?? restaurantData?.deliveryTime ?? "...";
  const restaurantName = restaurantData?.name ?? "...";
  const itemCount = (cart || []).reduce((s, c) => s + (parseInt(c.quantity, 10) || 0), 0);
  const subtotal = (cart || []).reduce(
    (s, c) => s + (parseFloat(c.price) || 0) * (parseInt(c.quantity, 10) || 0),
    0
  );

  // "Delivery to 12 Main St · Now · prep 15 min" (or the pickup / catering line).
  const when = timeMode === "scheduled" && scheduledFor
    ? scheduledFor.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "Now";
  const stopLabel = getCartMeta()?.stopLabel || null;
  const deliveryLine = cateringCart
    ? ["Catering order", cateringNotice(cateringSettingsOf(restaurantData).leadTimeMinutes) || "Order ahead"].join(" · ")
    : isPickup
      ? [`Pickup at ${stopLabel || (restaurantData?.address || "").split(",")[0] || restaurantName}`, when, `ready in ${prepTime} min`].join(" · ")
      : [
          `Delivery to ${shortAddress(location?.deliveryAddress) || location?.label || "your address"}`,
          when,
          `prep ${prepTime} min`,
        ].join(" · ");

  // Deals that can apply to this cart, and the line under the button for an
  // automatic deal the cart already qualifies for.
  const deals = dealsForCart(promos, { cateringCart, pickup: isPickup });
  const readyDeal = deals.find(
    (d) => !dealNeedsCode(d) && dealKind(d) === "discount" && (dealThreshold(d) || 0) <= subtotal
  );
  const note = readyDeal ? `${readyDeal.name} (${dealReward(readyDeal)}) can be applied at checkout` : null;

  return (
    <Box
      component="section"
      aria-labelledby="cart-title"
      sx={{ ...cartPanelSx(flat), p: 0, display: "flex", flexDirection: "column", maxHeight: maxHeight || "none", boxSizing: "border-box" }}
    >
      <Box sx={{ flexShrink: 0, px: PAD, pt: PAD }}>
        <CartHeading count={itemCount} id="cart-title" />
        <Box sx={{ mt: 1.5, px: 1.75, py: 1.25, borderRadius: RADIUS.controlLg, bgcolor: C.surface }}>
          <Box sx={{ ...TYPE.itemName, color: C.ink }}>{restaurantName}</Box>
          <Box sx={{ ...TYPE.meta, color: C.muted, mt: 0.25 }}>{deliveryLine}</Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          px: PAD,
          pb: 0.5,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { background: C.borderStrong, borderRadius: 6 },
        }}
      >
      <Box
        component="ul"
        aria-label="Items in your order"
        sx={{
          m: 0,
          mt: 1,
          p: 0,
        }}
      >
        {cart.map((foodItem) => (
          <CartItem
            as="li"
            key={`ITEM_${foodItem.key}`}
            quantity={foodItem.quantity}
            dealName={foodItem.title}
            foodTitle={foodItem.foodTitle}
            foodAltName1={foodItem.foodAltName1}
            foodAltName2={foodItem.foodAltName2}
            langDisplay={restaurantData?.menuLanguageDisplay || 'NONE'}
            variationTitle={foodItem.variationTitle}
            variationPrice={foodItem.variationPrice}
            unitPrice={foodItem.price}
            optionTitles={foodItem.optionTitles}
            optionsWithPrices={foodItem.optionsWithPrices}
            specialInstructions={foodItem.specialInstructions}
            dealPrice={((parseFloat(foodItem.price) || 0) * (parseInt(foodItem.quantity, 10) || 1)).toFixed(2)}
            addQuantity={() => addQuantity(foodItem.key)}
            removeQuantity={() => removeQuantity(foodItem.key)}
            onRemove={() => deleteItem(foodItem.key)}
            onEdit={() => {
              // Prefer the menu the line was ordered from so editing re-opens
              // the correct per-menu copy (price + linked groups).
              const food =
                menuData.foods.find((f) => f._id === foodItem._id && String(f.menuId) === String(foodItem.menuId))
                || menuData.foods.find((f) => f._id === foodItem._id);
              if (!food) return;
              pendingDeleteKeyRef.current = foodItem.key;
              // Only pass the groups this food is linked to. Without this, the
              // modal renders every group in the menu (e.g. an item with no
              // modifiers showed the pizza's topping/spice groups).
              const foodAddons = Array.isArray(food.addonGroupIds)
                ? menuData.addons.filter((a) => food.addonGroupIds.includes(a._id))
                : menuData.addons;
              setEditAddonData({
                food,
                addons: foodAddons,
                options: menuData.options,
                restaurant: restaurantData._id,
                image: restaurantData.image,
                initialCartItem: {
                  variation: foodItem.variation,
                  addons: foodItem.addons,
                  quantity: foodItem.quantity,
                  specialInstructions: foodItem.specialInstructions,
                },
              });
              setEditModalOpen(true);
            }}
          />
        ))}
      </Box>

      <DealProgress deals={deals} subtotal={subtotal} />
      {!cateringCart && <CartUpsell items={upsell} onAdd={onAddItem} />}
      <CartPromoCode restaurantId={restaurantData?._id || cartRestaurant} />
      </Box>

      <Box sx={{ flexShrink: 0, px: PAD, pb: PAD }}>
        <PricingView
          restaurantData={restaurantData}
          deliveryCharges={restaurantData?.deliveryCharges}
          note={note}
        />
      </Box>

      <VariationModal
        isVisible={editModalOpen}
        toggleModal={toggleEditModal}
        data={editAddonData}
        langDisplay={restaurantData?.menuLanguageDisplay || 'NONE'}
        replaceKey={pendingDeleteKeyRef.current}
      />
    </Box>
  );
}
export default React.memo(CartView);
